import secureLocalStorage from "react-secure-storage";
import React, { createContext, useState, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

// Create the auth context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Auth provider component
export const AuthProvider = ({ children }) => {
  // Primary state for user data - stored in memory, not localStorage
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Initialize auth state from localStorage on component mount
  // This is only used for persistence across page refreshes
  useEffect(() => {
    const initializeAuth = () => {
      try {
        // Check for auth token first
        const authToken = secureLocalStorage.getItem('authToken');
        
        if (!authToken) {
          setLoading(false);
          return;
        }
        
        // Get minimal routing data from localStorage
        const tenantKey = secureLocalStorage.getItem('tenantKey');
        const userKey = secureLocalStorage.getItem('userKey');
        const loggedClient = secureLocalStorage.getItem('loggedClient');
        
        // If we have the necessary keys, fetch the rest from secure context
        if ((tenantKey || userKey) && loggedClient) {
          // Create user data object with secure values
          const userData = {
            tenantKey,
            userKey,
            userName: secureLocalStorage.getItem('userName'),
            role: secureLocalStorage.getItem('role'),
            isPrimeSubsibed: secureLocalStorage.getItem('isPrime') === true,
            isTasksModuleEnabled: secureLocalStorage.getItem('isTasksModuleEnabled') === true,
            loggedClient,
            // Add authToken for API requests
            authToken
          };
          
          console.log('Auth initialized with:', userData);
          
          // Store in memory context
          setCurrentUser(userData);
        }
      } catch (error) {
        console.error('Error initializing auth state:', error);
      } finally {
        setLoading(false);
      }
    };
    
    initializeAuth();
  }, []);
  
  // Login function - primarily updates context state, with minimal localStorage
  const login = (userData) => {
    console.log('Login with userData:', userData);
    
    // Create a complete user object
    const user = {
      ...userData,
      // Ensure boolean values
      isPrimeSubsibed: userData.isPrimeSubsibed,
      isTasksModuleEnabled: userData.isTasksModuleEnabled,
      // Add timestamp for session management
      loginTime: new Date().toISOString()
    };
    
    // Store minimal data in localStorage for persistence
    // Only store what's needed for routing and basic identification
    secureLocalStorage.setItem('authToken', userData.authToken || secureLocalStorage.getItem('authToken'));
    if (userData.tenantKey) secureLocalStorage.setItem('tenantKey', userData.tenantKey);
    if (userData.userKey) secureLocalStorage.setItem('userKey', userData.userKey);
    if (userData.loggedClient) secureLocalStorage.setItem('loggedClient', userData.loggedClient);
    
    // Store role for route protection
    if (userData.role) secureLocalStorage.setItem('role', userData.role);
    
    // Store permission flags for UI rendering
    if (userData.isPrimeSubsibed !== undefined) {
      secureLocalStorage.setItem('isPrime', userData.isPrimeSubsibed);
    }
    if (userData.isTasksModuleEnabled !== undefined) {
      secureLocalStorage.setItem('isTasksModuleEnabled', userData.isTasksModuleEnabled);
    }
    
    // Store username for UI display
    if (userData.userName) secureLocalStorage.setItem('userName', userData.userName);
    
    // Update context state with complete user object
    setCurrentUser(user);
  };
  
  // Logout function - clears auth data
  const logout = () => {
    // Preserve minimal data for redirection
    const tenantKey = secureLocalStorage.getItem('tenantKey');
    const userKey = secureLocalStorage.getItem('userKey');
    const loggedClient = secureLocalStorage.getItem('loggedClient');
    
    // Clear all localStorage
    secureLocalStorage.clear();
    
    // Restore only what's needed for redirection
    if (tenantKey) secureLocalStorage.setItem('tenantKey', tenantKey);
    if (userKey) secureLocalStorage.setItem('userKey', userKey);
    if (loggedClient) secureLocalStorage.setItem('loggedClient', loggedClient);
    
    // Clear context state
    setCurrentUser(null);
  };
  
  // Check if user has specific permission - uses context state, not localStorage
  const hasPermission = (permission) => {
    // If no user in context, check localStorage as fallback
    if (!currentUser) {
      // Fallback to localStorage only for initial page load
      switch (permission) {
        case 'prime':
          return secureLocalStorage.getItem('isPrime') === true && secureLocalStorage.getItem('role') !== '-' && secureLocalStorage.getItem('role') !=='Project Admin';
        case 'tasks':
          return secureLocalStorage.getItem('isTasksModuleEnabled') === true;
        case 'admin': {
          const role = secureLocalStorage.getItem('role');
          return role === 'Project Admin' || role === undefined || role === null || role === '';
        }
        case 'superadmin': {
          const loggedClient = secureLocalStorage.getItem('loggedClient');
          return loggedClient === 'SuperAdmin';
        }
        default:
          return false;
      }
    }
    
    // Use in-memory context data for permission checks
    switch (permission) {
      case 'prime':
        return currentUser.isPrimeSubsibed && currentUser.role !=='-' && currentUser.role !=='Project Admin';
      case 'tasks':
        return currentUser.isTasksModuleEnabled;
      case 'admin':
        return currentUser.role === 'Project Admin' || !currentUser.role;
      case 'superadmin':
        return currentUser.loggedClient === 'SuperAdmin';
      default:
        return false;
    }
  };
  
  // Get auth token for API requests
  const getAuthToken = () => {
    return currentUser?.authToken || secureLocalStorage.getItem('authToken');
  };
  
  // Value to be provided to consumers
  const value = {
    currentUser,
    login,
    logout,
    hasPermission,
    getAuthToken,
    loading
  };
  
  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;